import { useMemo, useState } from 'react';

import { ApiError, api } from '../../api/client';
import type { AvailabilityItem, AvailabilityStatus } from '../../api/types';
import { Banner, Card, Empty, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../hooks/useAuth';

/**
 * Écran principal du membre (brief §4), rempli depuis un téléphone.
 *
 * Deux partis pris d'usage : la liste entière se valide en **un seul envoi**
 * (une requête par date rendrait l'écran pénible), et l'absence de réponse est
 * un état visible — pas un « non » déguisé (ambiguïté A1).
 */
export default function MyAvailability() {
  const { profile } = useAuth();
  const personId = profile?.person_id ?? null;

  const [draft, setDraft] = useState<Record<number, AvailabilityStatus>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const events = useAsync(() => api.events(new Date().toISOString().slice(0, 10)), []);
  const answers = useAsync(
    () => (personId === null ? Promise.resolve([]) : api.availability(personId)),
    [personId],
  );

  const current = useMemo(() => {
    const base: Record<number, AvailabilityStatus> = {};
    for (const row of answers.data ?? []) base[row.event_id] = row.status;
    return { ...base, ...draft };
  }, [answers.data, draft]);

  if (personId === null) {
    return (
      <Banner kind="info">
        Votre compte n'est pas encore rattaché à une personne de l'équipe. Demandez à un
        responsable de faire le lien, puis reconnectez-vous.
      </Banner>
    );
  }

  const upcoming = events.data ?? [];
  const pending = upcoming.filter((event) => !current[event.id] || current[event.id] === 'UNKNOWN');

  // Déclaré en expression, après la garde ci-dessus : une fonction hoistée
  // ferait perdre à TypeScript le fait que `personId` n'est plus nul.
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    const items: AvailabilityItem[] = Object.entries(draft).map(([eventId, status]) => ({
      event_id: Number(eventId),
      status,
      note: null,
    }));
    try {
      await api.replaceAvailability(personId, items);
      setDraft({});
      answers.reload();
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h1>Mes disponibilités</h1>
      <p className="muted">
        Indiquez pour chaque date si vous êtes disponible. Vous pouvez tout remplir puis
        enregistrer en une fois.
      </p>

      {error && <Banner kind="error">{error}</Banner>}
      {saved && <Banner kind="success">Vos disponibilités sont enregistrées.</Banner>}
      {events.error && <Banner kind="error">{events.error}</Banner>}

      {pending.length > 0 && (
        <Banner kind="info">
          {pending.length} date{pending.length > 1 ? 's' : ''} sans réponse. Sans réponse, le
          responsable ne sait pas s'il peut compter sur vous.
        </Banner>
      )}

      <Card>
        {events.loading ? (
          <p className="muted">Chargement des dates…</p>
        ) : upcoming.length === 0 ? (
          <Empty>Aucune date à venir pour l'instant.</Empty>
        ) : (
          <div className="stack">
            {upcoming.map((event) => (
              <div key={event.id}>
                <div className="spread" style={{ marginBottom: 6 }}>
                  <strong>{formatDate(event.starts_at)}</strong>
                  <span className="muted">{secondaryLabel(event.title, event.starts_at)}</span>
                </div>
                <div className="choice-group" role="group" aria-label={event.title}>
                  {(
                    [
                      ['AVAILABLE', 'Disponible', 'yes'],
                      ['UNAVAILABLE', 'Indisponible', 'no'],
                      ['UNKNOWN', 'Je ne sais pas', 'maybe'],
                    ] as const
                  ).map(([status, label, tone]) => (
                    <button
                      key={status}
                      type="button"
                      className={`choice ${tone}`}
                      aria-pressed={current[event.id] === status}
                      onClick={() => setDraft((previous) => ({ ...previous, [event.id]: status }))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="spread">
        <span className="muted">
          {Object.keys(draft).length === 0
            ? 'Aucune modification à enregistrer.'
            : `${Object.keys(draft).length} modification(s) en attente.`}
        </span>
        <button
          className="primary"
          onClick={save}
          disabled={busy || Object.keys(draft).length === 0}
        >
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </>
  );
}
